/**
 * Storage Service
 *
 * Abstraction layer for localStorage to provide:
 * - Type-safe access to stored values
 * - Centralized key management
 * - Easy testing and mocking
 * - Future migration to alternative storage (sessionStorage, IndexedDB)
 *
 * @example
 * // Setting and getting tokens
 * StorageService.setToken('abc123');
 * const token = StorageService.getToken();
 *
 * // Working with user data
 * StorageService.setUserData({
 *   id: '123',
 *   email: 'user@pwc.com',
 *   name: 'John Doe',
 *   los: 'Advisory',
 *   roles: ['user', 'admin']
 * });
 * const userData = StorageService.getUserData();
 */

import { UserData } from '../types/api';

/**
 * Storage keys constants
 * Centralized to avoid typos and make refactoring easier
 */
const STORAGE_KEYS = {
  TOKEN: 'token',
  REFRESH_TOKEN: 'refresh-token',
  USER_EMAIL: 'userEmail',
  USER_DISPLAY_NAME: 'displayName',
  USER_LOS: 'los',
  USER_ROLES: 'roles',
  USER_ID: 'id',
  WELCOME_CLOSED: 'welcomeClosed',
  CODE_VERIFIER: 'auth.codeVerifier',
  OAUTH_STATE: 'auth.oauthState',
} as const;

class StorageService {
  // ============================================
  // TOKEN MANAGEMENT
  // ============================================

  /**
   * Set the access token
   */
  static setToken(token: string): void {
    localStorage.setItem(STORAGE_KEYS.TOKEN, token);
  }

  /**
   * Get the access token
   * @returns The access token or null if not found
   */
  static getToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.TOKEN);
  }

  /**
   * Check if a valid token exists
   */
  static hasToken(): boolean {
    return !!this.getToken();
  }

  /**
   * Remove the access token
   */
  static removeToken(): void {
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
  }

  // ============================================
  // REFRESH TOKEN MANAGEMENT
  // ============================================

  /**
   * Set the refresh token
   */
  static setRefreshToken(token: string): void {
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, token);
  }

  /**
   * Get the refresh token
   * @returns The refresh token or null if not found
   */
  static getRefreshToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  }

  /**
   * Check if a refresh token exists
   */
  static hasRefreshToken(): boolean {
    return !!this.getRefreshToken();
  }

  /**
   * Remove the refresh token
   */
  static removeRefreshToken(): void {
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  }

  // ============================================
  // USER DATA MANAGEMENT
  // ============================================

  /**
   * Set user data
   * Stores all user information in localStorage
   */
  static setUserData(data: UserData): void {
    localStorage.setItem(STORAGE_KEYS.USER_EMAIL, data.email);
    localStorage.setItem(STORAGE_KEYS.USER_DISPLAY_NAME, data.name);
    localStorage.setItem(STORAGE_KEYS.USER_LOS, data.los);
    localStorage.setItem(STORAGE_KEYS.USER_ROLES, JSON.stringify(data.roles));
    localStorage.setItem(STORAGE_KEYS.USER_ID, data.id);
  }

  /**
   * Get user data
   * @returns UserData object or null if user data is incomplete
   */
  static getUserData(): UserData | null {
    const email = localStorage.getItem(STORAGE_KEYS.USER_EMAIL);
    if (!email) return null;

    return {
      id: localStorage.getItem(STORAGE_KEYS.USER_ID) || '',
      email,
      name: localStorage.getItem(STORAGE_KEYS.USER_DISPLAY_NAME) || '',
      los: localStorage.getItem(STORAGE_KEYS.USER_LOS) || '',
      roles: this.parseRoles(),
    };
  }

  /**
   * Parse roles from localStorage
   * @returns Array of role strings
   */
  private static parseRoles(): string[] {
    try {
      const rolesStr = localStorage.getItem(STORAGE_KEYS.USER_ROLES);
      return rolesStr ? JSON.parse(rolesStr) : [];
    } catch (error) {
      console.error('Failed to parse user roles');
      return [];
    }
  }

  /**
   * Get user email
   */
  static getUserEmail(): string | null {
    return localStorage.getItem(STORAGE_KEYS.USER_EMAIL);
  }

  /**
   * Check if user data exists
   */
  static hasUserData(): boolean {
    return !!localStorage.getItem(STORAGE_KEYS.USER_EMAIL);
  }

  // ============================================
  // UI STATE MANAGEMENT
  // ============================================

  /**
   * Set welcome screen closed state
   */
  static setWelcomeClosed(closed: boolean): void {
    localStorage.setItem(STORAGE_KEYS.WELCOME_CLOSED, String(closed));
  }

  /**
   * Check if welcome screen is closed
   */
  static isWelcomeClosed(): boolean {
    return localStorage.getItem(STORAGE_KEYS.WELCOME_CLOSED) === 'true';
  }

  // ============================================
  // OAUTH CODE VERIFIER (PKCE)
  // ============================================

  /**
   * Set OAuth code verifier for PKCE flow
   */
  static setCodeVerifier(verifier: string): void {
    localStorage.setItem(STORAGE_KEYS.CODE_VERIFIER, verifier);
  }

  /**
   * Get OAuth code verifier
   */
  static getCodeVerifier(): string | null {
    return localStorage.getItem(STORAGE_KEYS.CODE_VERIFIER);
  }

  /**
   * Remove OAuth code verifier
   */
  static removeCodeVerifier(): void {
    localStorage.removeItem(STORAGE_KEYS.CODE_VERIFIER);
  }

  // ============================================
  // OAUTH STATE (CSRF PROTECTION)
  // ============================================

  static setOAuthState(state: string): void {
    localStorage.setItem(STORAGE_KEYS.OAUTH_STATE, state);
  }

  static getOAuthState(): string | null {
    return localStorage.getItem(STORAGE_KEYS.OAUTH_STATE);
  }

  static removeOAuthState(): void {
    localStorage.removeItem(STORAGE_KEYS.OAUTH_STATE);
  }

  // ============================================
  // CLEAR / CLEANUP
  // ============================================

  /**
   * Clear all authentication data
   * Use this on logout or auth failure
   */
  static clearAuth(): void {
    this.removeToken();
    this.removeRefreshToken();
    this.removeCodeVerifier();
    this.removeOAuthState();
  }

  /**
   * Clear all user data
   */
  static clearUserData(): void {
    localStorage.removeItem(STORAGE_KEYS.USER_EMAIL);
    localStorage.removeItem(STORAGE_KEYS.USER_DISPLAY_NAME);
    localStorage.removeItem(STORAGE_KEYS.USER_LOS);
    localStorage.removeItem(STORAGE_KEYS.USER_ROLES);
    localStorage.removeItem(STORAGE_KEYS.USER_ID);
  }

  /**
   * Clear all data (auth + user + UI state)
   * Use this for complete logout
   */
  static clearAll(): void {
    this.clearAuth();
    this.clearUserData();
    localStorage.removeItem(STORAGE_KEYS.WELCOME_CLOSED);
  }

  /**
   * Clear specific key (for testing or special cases)
   */
  static clear(key: string): void {
    localStorage.removeItem(key);
  }
}

export default StorageService;
